import { useEffect, useRef } from 'react';
import { getActiveClipEffectItems, normalizeClipEffectItems, type IVideoClip } from '@packages/global';

interface UseClipEffectsPlayerOptions {
  clip: IVideoClip | null;
  localTime: number;
  isPlaying: boolean;
  clipDuration?: number;
}

export function useClipEffectsPlayer({
  clip,
  localTime,
  isPlaying,
  clipDuration,
}: UseClipEffectsPlayerOptions) {
  const playersRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  const triggeredRef = useRef<Set<string>>(new Set());
  const prevLocalTimeRef = useRef(0);
  const prevClipIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevClipIdRef.current !== clip?.id) {
      playersRef.current.forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });
      triggeredRef.current.clear();
      prevLocalTimeRef.current = 0;
      prevClipIdRef.current = clip?.id || null;
    }
  }, [clip?.id]);

  useEffect(() => {
    if (!clip || !isPlaying) {
      playersRef.current.forEach((audio) => audio.pause());
      return;
    }

    if (localTime < prevLocalTimeRef.current) {
      triggeredRef.current.clear();
      playersRef.current.forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });
    }

    const activeSoundEffects = getActiveClipEffectItems(clip.effectItems, localTime, clipDuration)
      .filter((item) => item.sound?.audioUrl);

    activeSoundEffects.forEach((item) => {
      if (triggeredRef.current.has(item.id) || !item.sound?.audioUrl) return;

      let player = playersRef.current.get(item.id);
      if (!player) {
        player = new Audio(item.sound.audioUrl);
        playersRef.current.set(item.id, player);
      } else if (player.src !== item.sound.audioUrl) {
        player.pause();
        player = new Audio(item.sound.audioUrl);
        playersRef.current.set(item.id, player);
      }

      player.volume = item.sound.volume ?? 1;
      player.currentTime = 0;
      player.play().catch(() => undefined);
      triggeredRef.current.add(item.id);
    });

    const validIds = new Set(normalizeClipEffectItems(clip.effectItems, clipDuration).map((item) => item.id));
    triggeredRef.current.forEach((id) => {
      if (!validIds.has(id)) {
        triggeredRef.current.delete(id);
        const player = playersRef.current.get(id);
        player?.pause();
        playersRef.current.delete(id);
      }
    });

    prevLocalTimeRef.current = localTime;
  }, [clip, localTime, isPlaying, clipDuration]);

  useEffect(() => () => {
    playersRef.current.forEach((audio) => audio.pause());
    playersRef.current.clear();
    triggeredRef.current.clear();
  }, []);
}
